import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from "examples/Footer";
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

export default function AddDefaultRoles() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const navigate = useNavigate();
    const [permissions, setPermissions] = useState([]);

    const fetchPermissions = async () => {
        try {
            const response = await axios.get(`${apiUrl}auth-service/permission`,
                {
                    headers: { 'Authorization': `Bearer ${bearerToken}` }
                });
            if (response.data?.success) {
                setPermissions(response.data.data);
            } else throw new Error(response.data?.message || 'Error in fetching permissions');
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        }
    };

    useEffect(() => {
        fetchPermissions();
    }, [bearerToken]);

    const onSubmit = async (values, { resetForm }) => {
        try {
            const response = await axios.post(`${apiUrl}auth-service/default-role`, values, {
                headers: { 'Authorization': `Bearer ${bearerToken}` },
            });
            if (response.data?.success && response.data?.data) {
                toast.info(response.data.message || 'Permission created successfully');
            } else {
                throw new Error("Failed to create permission");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
        } finally {
            resetForm();
            setTimeout(() => {
                navigate(-1);
            }, 1500);
        }
    };

    const newDefaultRole = useFormik({
        initialValues: {
            name: '',
            permissions: [],
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Required'),
        }),
        onSubmit,
    });

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <form onSubmit={newDefaultRole.handleSubmit}>
                            <Card>
                                <MDBox
                                    mx={2}
                                    mt={-3}
                                    py={3}
                                    px={2}
                                    variant="gradient"
                                    bgColor="info"
                                    borderRadius="lg"
                                    coloredShadow="info"
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <MDTypography variant="h6" color="white">
                                        Add Default Roles
                                    </MDTypography>
                                </MDBox>
                                <MDBox p={2}>
                                    <MDBox p={1}>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            id="name"
                                            label="Name"
                                            name="name"
                                            value={newDefaultRole.values.name}
                                            onChange={newDefaultRole.handleChange}
                                            onBlur={newDefaultRole.handleBlur}
                                            error={newDefaultRole.touched.name && Boolean(newDefaultRole.errors.name)}
                                            helperText={newDefaultRole.touched.name && newDefaultRole.errors.name}
                                        />
                                    </MDBox>
                                    <MDBox p={1}>
                                        <FormControl fullWidth>
                                            <InputLabel>Select Permissions</InputLabel>
                                            <Select
                                                label="Select Permissions"
                                                name="permissions"
                                                multiple
                                                value={newDefaultRole.values.permissions}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    newDefaultRole.setFieldValue('permissions', typeof value === 'string' ? value.split(',') : value);
                                                }}
                                                onBlur={newDefaultRole.handleBlur}
                                                sx={{ height: '45px' }}
                                                renderValue={(selected) =>
                                                    permissions
                                                        .filter((permission) => selected.includes(permission.id))
                                                        .map((permission) => permission.name)
                                                        .join(', ')
                                                }
                                            >
                                                {permissions?.map((permission) => (
                                                    <MenuItem key={permission.id} value={permission.id}>
                                                        {permission.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {newDefaultRole.touched.permissions && newDefaultRole.errors.permissions && (
                                                <FormHelperText>{newDefaultRole.errors.permissions}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </MDBox>
                                    <MDBox p={1}>
                                        <MDButton color="info" type="submit">
                                            Save
                                        </MDButton>
                                    </MDBox>
                                </MDBox>
                            </Card>
                        </form>
                    </Grid>
                </Grid>
            </MDBox>
            <Footer />
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </DashboardLayout>
    );
}